/**
 * Contact links.
 *
 * Kept apart from the formatters because these are decisions, not formatting:
 * whether a number can be dialled internationally, and what a message opens
 * with, are judgements about the business.
 */

/**
 * Digits only, for wa.me.
 *
 * WhatsApp's Click to Chat takes the number bare -- no `+`, spaces or dashes --
 * and needs a country code. "+91 98765 43210" and "098765 43210" are the same
 * number to a person and different strings to an API, so normalising here is
 * what makes "message them on WhatsApp" work at all.
 *
 * Returns null when the result is too short to be an international number. A
 * five-digit local extension cannot be dialled from a phone abroad, and
 * producing `wa.me/12345` opens somebody else's chat.
 */
export function whatsappNumber(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  // 7 is the shortest ITU country code plus a subscriber number that can be
  // unique; below that it is a local extension.
  return digits.length >= 7 ? digits : null;
}

export function telLink(phone: string | null | undefined): string | null {
  const trimmed = phone?.trim();
  return trimmed ? `tel://${trimmed.replace(/[^\d+]/g, "")}` : null;
}

/**
 * A Click to Chat link with the message pre-filled.
 *
 * `wa.me` opens WhatsApp with the text ready to send; the person still presses
 * send themselves. That is deliberate -- we have no WhatsApp Business API
 * credentials, and quietly pretending otherwise would produce a button that
 * silently does nothing in production.
 */
export function whatsappLink(
  phone: string | null | undefined,
  message?: string | null,
): string | null {
  const number = whatsappNumber(phone);
  if (!number) return null;

  const base = `https://wa.me/${number}`;
  const text = message?.trim();
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

/** The greeting a salon or clinic actually opens a message with. */
export function defaultGreeting(firstName?: string | null): string {
  const name = firstName?.trim();
  return name ? `Hi ${name}, ` : "Hi, ";
}